import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { saveFloorPlanToSupabase, saveInventoryToSupabase, loadFloorPlanFromSupabase, loadInventoryFromSupabase, type PlanType } from '../utils/supabaseStorage';
import type { SavedFloorPlanData, SavedPropertyInventoryData } from '../utils/fileUtils';

export interface OfflineFileMetadata {
  id: string;
  name: string;
  type: 'floorplan' | 'inventory';
  planType?: PlanType; // Only for floorplans: 'energy' (EPC) or 'estate' (property inventory)
  isAvailableOffline: boolean;
  lastSyncedAt?: string;
  lastModifiedAt: string;
  cloudId?: string; // Supabase ID if synced
  localPath: string;
  syncStatus: 'synced' | 'pending' | 'conflict' | 'local-only';
  conflictData?: {
    localVersion: string;
    cloudVersion: string;
    conflictDetectedAt: string;
  };
}

export interface SyncQueueItem {
  id: string;
  type: 'floorplan' | 'inventory';
  planType?: PlanType; // Only for floorplans
  action: 'upload' | 'download' | 'delete';
  priority: 'high' | 'medium' | 'low';
  data?: any;
  fileName: string;
  cloudId?: string;
  attempts: number;
  lastAttempt?: string;
  error?: string;
  createdAt: string;
}

export interface OfflineConfig {
  maxRetries: number;
  retryDelay: number;
  syncBatchSize: number;
  maxOfflineStorage: number; // MB
  autoSyncInterval: number; // minutes
}

class OfflineStorageService {
  private static instance: OfflineStorageService;
  private syncQueue: SyncQueueItem[] = [];
  private offlineFiles: Map<string, OfflineFileMetadata> = new Map();
  private isOnline: boolean = navigator.onLine;
  private isSyncing: boolean = false;
  private syncListeners: Set<(status: SyncStatus) => void> = new Set();
  private config: OfflineConfig = {
    maxRetries: 3,
    retryDelay: 5000,
    syncBatchSize: 5,
    maxOfflineStorage: 500, // 500MB
    autoSyncInterval: 5 // 5 minutes
  };

  private constructor() {
    this.initializeNetworkListeners();
    this.loadOfflineMetadata();
    this.loadSyncQueue();
    this.startAutoSync();
  }

  public static getInstance(): OfflineStorageService {
    if (!OfflineStorageService.instance) {
      OfflineStorageService.instance = new OfflineStorageService();
    }
    return OfflineStorageService.instance;
  }

  // Network status management
  private initializeNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('[OfflineStorage] Network online - starting sync');
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('[OfflineStorage] Network offline - queuing future syncs');
    });
  }

  public getNetworkStatus(): boolean {
    return this.isOnline;
  }

  // Sync status management
  public addSyncListener(listener: (status: SyncStatus) => void) {
    this.syncListeners.add(listener);
  }

  public removeSyncListener(listener: (status: SyncStatus) => void) {
    this.syncListeners.delete(listener);
  }

  private notifySyncListeners(status: SyncStatus) {
    this.syncListeners.forEach(listener => listener(status));
  }

  // File operations
  public async saveFileOffline(
    fileName: string,
    data: SavedFloorPlanData | SavedPropertyInventoryData,
    type: 'floorplan' | 'inventory',
    cloudId?: string
  ): Promise<string> {
    const fileId = cloudId || `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const localPath = `${type}s/${fileName}.json`;
    
    try {
      // Save to local storage
      await this.writeFileToLocal(localPath, data);
      
      // Update metadata
      const metadata: OfflineFileMetadata = {
        id: fileId,
        name: fileName,
        type,
        isAvailableOffline: true,
        lastModifiedAt: new Date().toISOString(),
        cloudId,
        localPath,
        syncStatus: cloudId ? 'synced' : 'local-only'
      };
      
      this.offlineFiles.set(fileId, metadata);
      await this.saveOfflineMetadata();
      
      // Queue for sync if online
      if (this.isOnline && !cloudId) {
        this.addToSyncQueue({
          id: fileId,
          type,
          action: 'upload',
          priority: 'medium',
          data,
          fileName,
          attempts: 0,
          createdAt: new Date().toISOString()
        });
      }
      
      return fileId;
    } catch (error) {
      console.error('[OfflineStorage] Error saving file offline:', error);
      throw error;
    }
  }

  public async loadFileOffline(fileId: string): Promise<SavedFloorPlanData | SavedPropertyInventoryData | null> {
    const metadata = this.offlineFiles.get(fileId);
    if (!metadata) {
      console.warn('[OfflineStorage] File metadata not found:', fileId);
      return null;
    }

    try {
      const data = await this.readFileFromLocal(metadata.localPath);
      return data;
    } catch (error) {
      console.error('[OfflineStorage] Error loading file offline:', error);
      return null;
    }
  }

  public async toggleOfflineAvailability(fileId: string, available: boolean): Promise<void> {
    const metadata = this.offlineFiles.get(fileId);
    if (!metadata) {
      throw new Error(`File not found: ${fileId}`);
    }

    if (available && !metadata.isAvailableOffline) {
      // Download for offline access
      if (metadata.cloudId && this.isOnline) {
        await this.downloadFileForOffline(metadata.cloudId, metadata.type, metadata.name);
      }
    } else if (!available && metadata.isAvailableOffline) {
      // Remove from offline storage
      await this.removeFromOfflineStorage(fileId);
    }
  }

  private async downloadFileForOffline(cloudId: string, type: 'floorplan' | 'inventory', fileName: string): Promise<void> {
    try {
      let data: SavedFloorPlanData | SavedPropertyInventoryData | null = null;
      
      if (type === 'floorplan') {
        data = await loadFloorPlanFromSupabase(cloudId);
      } else {
        data = await loadInventoryFromSupabase(cloudId);
      }

      if (data) {
        const localPath = `${type}s/${fileName}.json`;
        await this.writeFileToLocal(localPath, data);
        
        const metadata: OfflineFileMetadata = {
          id: cloudId,
          name: fileName,
          type,
          isAvailableOffline: true,
          lastSyncedAt: new Date().toISOString(),
          lastModifiedAt: new Date().toISOString(),
          cloudId,
          localPath,
          syncStatus: 'synced'
        };
        
        this.offlineFiles.set(cloudId, metadata);
        await this.saveOfflineMetadata();
      }
    } catch (error) {
      console.error('[OfflineStorage] Error downloading file for offline:', error);
      throw error;
    }
  }

  private async removeFromOfflineStorage(fileId: string): Promise<void> {
    const metadata = this.offlineFiles.get(fileId);
    if (!metadata) return;

    try {
      // Remove local file
      await Filesystem.deleteFile({
        path: metadata.localPath,
        directory: Directory.Data
      });
      
      // Update metadata
      metadata.isAvailableOffline = false;
      this.offlineFiles.set(fileId, metadata);
      await this.saveOfflineMetadata();
    } catch (error) {
      console.error('[OfflineStorage] Error removing file from offline storage:', error);
    }
  }

  // Sync queue management
  private addToSyncQueue(item: SyncQueueItem) {
    // Remove existing item with same id to avoid duplicates
    this.syncQueue = this.syncQueue.filter(qItem => qItem.id !== item.id);
    this.syncQueue.push(item);
    this.saveSyncQueue();
    
    if (this.isOnline) {
      this.processSyncQueue();
    }
  }

  private async processSyncQueue(): Promise<void> {
    if (this.isSyncing || !this.isOnline || this.syncQueue.length === 0) {
      return;
    }

    this.isSyncing = true;
    this.notifySyncListeners({ status: 'syncing', progress: 0, total: this.syncQueue.length });

    const batch = this.syncQueue.slice(0, this.config.syncBatchSize);
    let processed = 0;

    for (const item of batch) {
      try {
        await this.processSyncItem(item);
        // Remove from queue on success
        this.syncQueue = this.syncQueue.filter(qItem => qItem.id !== item.id);
        processed++;
        
        this.notifySyncListeners({ 
          status: 'syncing', 
          progress: processed, 
          total: this.syncQueue.length + processed 
        });
      } catch (error) {
        console.error('[OfflineStorage] Sync item failed:', error);
        item.attempts++;
        item.lastAttempt = new Date().toISOString();
        item.error = (error as Error).message;
        
        if (item.attempts >= this.config.maxRetries) {
          console.error('[OfflineStorage] Max retries reached for item:', item.id);
          // Keep in queue but mark as failed
          item.error = `Max retries exceeded: ${item.error}`;
        }
      }
    }

    await this.saveSyncQueue();
    this.isSyncing = false;
    
    this.notifySyncListeners({ 
      status: this.syncQueue.length > 0 ? 'pending' : 'idle', 
      progress: processed, 
      total: processed 
    });

    // Continue processing if there are more items
    if (this.syncQueue.length > 0) {
      setTimeout(() => this.processSyncQueue(), this.config.retryDelay);
    }
  }

  private async processSyncItem(item: SyncQueueItem): Promise<void> {
    const metadata = this.offlineFiles.get(item.id);
    
    switch (item.action) {
      case 'upload':
        if (metadata && item.data) {
          let cloudId: string;
          
          if (item.type === 'floorplan') {
            cloudId = await saveFloorPlanToSupabase(item.fileName, item.data, item.cloudId);
          } else {
            cloudId = await saveInventoryToSupabase(item.fileName, item.data, item.cloudId);
          }
          
          // Update metadata
          metadata.cloudId = cloudId;
          metadata.syncStatus = 'synced';
          metadata.lastSyncedAt = new Date().toISOString();
          this.offlineFiles.set(item.id, metadata);
          await this.saveOfflineMetadata();
        }
        break;
        
      case 'download':
        if (item.cloudId) {
          await this.downloadFileForOffline(item.cloudId, item.type, item.fileName);
        }
        break;
        
      case 'delete':
        // Handle deletion sync
        break;
    }
  }

  // File system operations
  private async writeFileToLocal(path: string, data: any): Promise<void> {
    const directory = path.split('/')[0];
    
    // Ensure directory exists
    try {
      await Filesystem.mkdir({
        path: directory,
        directory: Directory.Data,
        recursive: true
      });
    } catch (error) {
      // Directory might already exist
    }
    
    await Filesystem.writeFile({
      path,
      data: JSON.stringify(data, null, 2),
      directory: Directory.Data,
      encoding: Encoding.UTF8
    });
  }

  private async readFileFromLocal(path: string): Promise<any> {
    const result = await Filesystem.readFile({
      path,
      directory: Directory.Data,
      encoding: Encoding.UTF8
    });
    
    return JSON.parse(result.data as string);
  }

  // Metadata management
  private async saveOfflineMetadata(): Promise<void> {
    const metadata = Array.from(this.offlineFiles.values());
    await this.writeFileToLocal('offline_metadata.json', metadata);
  }

  private async loadOfflineMetadata(): Promise<void> {
    try {
      const metadata = await this.readFileFromLocal('offline_metadata.json');
      this.offlineFiles.clear();
      
      if (Array.isArray(metadata)) {
        metadata.forEach(item => {
          this.offlineFiles.set(item.id, item);
        });
      }
    } catch (error) {
      console.log('[OfflineStorage] No existing metadata found, starting fresh');
    }
  }

  private async saveSyncQueue(): Promise<void> {
    await this.writeFileToLocal('sync_queue.json', this.syncQueue);
  }

  private async loadSyncQueue(): Promise<void> {
    try {
      const queue = await this.readFileFromLocal('sync_queue.json');
      this.syncQueue = Array.isArray(queue) ? queue : [];
    } catch (error) {
      console.log('[OfflineStorage] No existing sync queue found, starting fresh');
      this.syncQueue = [];
    }
  }

  // Auto-sync management
  private startAutoSync() {
    setInterval(() => {
      if (this.isOnline && this.syncQueue.length > 0) {
        this.processSyncQueue();
      }
    }, this.config.autoSyncInterval * 60 * 1000);
  }

  // Public API
  public getOfflineFiles(): OfflineFileMetadata[] {
    return Array.from(this.offlineFiles.values());
  }

  public getSyncQueue(): SyncQueueItem[] {
    return [...this.syncQueue];
  }

  public getSyncStatus(): SyncStatus {
    if (this.isSyncing) {
      return { status: 'syncing', progress: 0, total: this.syncQueue.length };
    } else if (this.syncQueue.length > 0) {
      return { status: 'pending', progress: 0, total: this.syncQueue.length };
    } else {
      return { status: 'idle', progress: 0, total: 0 };
    }
  }

  public async forceSyncFile(fileId: string): Promise<void> {
    const metadata = this.offlineFiles.get(fileId);
    if (!metadata) {
      throw new Error(`File not found: ${fileId}`);
    }

    if (!this.isOnline) {
      throw new Error('No internet connection available');
    }

    const data = await this.loadFileOffline(fileId);
    if (!data) {
      throw new Error('Could not load file data');
    }

    this.addToSyncQueue({
      id: fileId,
      type: metadata.type,
      action: 'upload',
      priority: 'high',
      data,
      fileName: metadata.name,
      cloudId: metadata.cloudId,
      attempts: 0,
      createdAt: new Date().toISOString()
    });
  }

  public async clearSyncQueue(): Promise<void> {
    this.syncQueue = [];
    await this.saveSyncQueue();
  }
}

export interface SyncStatus {
  status: 'idle' | 'syncing' | 'pending' | 'error';
  progress: number;
  total: number;
  error?: string;
}

export default OfflineStorageService; 